import { useTranslation } from 'react-i18next';

import { NgButton, PanelInfoWithIcon } from '@/ui';
import type { CallerConnectedStatus, LiveVideo } from '@/signalr';
import { IncidentState } from '@/signalr';
import { activateLiveVideo } from '@ngsos/portal-core/commands';
import { MediasoupVideoBox } from './MediasoupVideoBox';
import { VideoRecordingsBox } from './VideoRecordingsBox';

export type VideoBoxProps = {
  incidentId: string;
  incidentState: IncidentState | null;
  incidentIsReadOnly: boolean;
  incidentHasPhoneNumber: boolean;
  hasAppleEedVideoCapability: boolean;
  allowAppleEedVideoActions: boolean;
  appleEedVideoCallId?: string | null;
  liveVideos: readonly LiveVideo[];
  activeLiveVideoId: string | null;
  videoExpanded: boolean;
  onVideoExpandedChanged: (expanded: boolean) => void;
  callerConnectedStatus: CallerConnectedStatus;
};

async function startVideo(incidentId: string): Promise<void> {
  await activateLiveVideo(incidentId);
}

export function VideoBox({
  incidentId,
  incidentState,
  incidentIsReadOnly,
  incidentHasPhoneNumber,
  hasAppleEedVideoCapability,
  allowAppleEedVideoActions,
  appleEedVideoCallId,
  liveVideos,
  activeLiveVideoId,
  videoExpanded,
  onVideoExpandedChanged,
}: VideoBoxProps) {
  const { t } = useTranslation();

  const allowStartingVideo = incidentHasPhoneNumber;
  const activeLiveVideo = liveVideos.find((video) => video.id === activeLiveVideoId) ?? null;

  if (incidentIsReadOnly && liveVideos.length > 0) {
    return (
      <div className="video-box p-3 justify-content-center">
        <VideoRecordingsBox incidentId={incidentId} liveVideos={liveVideos} />
      </div>
    );
  }

  if (incidentState === IncidentState.Open && !incidentIsReadOnly) {
    if (activeLiveVideo !== null) {
      return (
        <MediasoupVideoBox
          key={activeLiveVideo.id}
          incidentId={incidentId}
          liveVideo={activeLiveVideo}
          allowAppleEedVideoActions={allowAppleEedVideoActions}
          appleEedVideoCallId={appleEedVideoCallId}
          videoExpanded={videoExpanded}
          onVideoExpandedChanged={onVideoExpandedChanged}
        />
      );
    }

    return (
      <div className="video-box">
        <VideoRecordingsBox incidentId={incidentId} liveVideos={liveVideos} allowDivider />

        {hasAppleEedVideoCapability ? (
          <PanelInfoWithIcon icon="fal fa-video" text={t('Incident_Video_AppleEedVideoCapable')}>
            {allowStartingVideo && (
              <NgButton
                text={t('Incident_Button_InitiateVideo')}
                icon="fal fa-video-plus"
                size="small"
                onClick={() => startVideo(incidentId)}
              />
            )}
          </PanelInfoWithIcon>
        ) : (
          <PanelInfoWithIcon icon="fal fa-video-slash" text={t('Incident_Video_VideoInactive')}>
            {allowStartingVideo && (
              <NgButton
                text={t('Incident_Button_InitiateVideo')}
                icon="fal fa-video-plus"
                size="small"
                onClick={() => startVideo(incidentId)}
              />
            )}
          </PanelInfoWithIcon>
        )}
      </div>
    );
  }

  return null;
}
